import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { clearToken } from "../lib/api";
import BrandHeader from "../components/BrandHeader";
import BrandFooter from "../components/BrandFooter";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Building2, Loader2, ChevronRight, Shield } from "lucide-react";

const STATUS = {
  not_started: { label: "Not started", cls: "bg-slate-100 text-slate-600 border border-slate-200" },
  in_progress: { label: "In progress", cls: "bg-amber-50 text-amber-700 border border-amber-200" },
  submitted:   { label: "Submitted",   cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
};

export default function ClientPortal() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([api.get("/client/onboardings"), api.get("/auth/me")]);
        setItems(a.data.onboardings || []);
        setMe(b.data);
      } catch (e) {
        if (e?.response?.status === 401) { clearToken(); nav("/login"); }
      } finally { setLoading(false); }
    })();
    // Mount-only fetch — `api`, `clearToken`, `nav` are stable singletons.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="animate-spin text-brand-primary"/></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <BrandHeader showLogout onLogoClick={() => nav("/portal")}>
        {me && <span className="text-sm text-white/70 hidden sm:inline mr-2" data-testid="client-email">{me.email}</span>}
      </BrandHeader>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="mb-6 p-3 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 text-sm flex items-start gap-2" data-testid="client-privacy-banner">
          <Shield size={16} className="text-emerald-600 shrink-0 mt-0.5"/>
          <div>
            <strong className="font-medium">Private workspace.</strong>{" "}
            You can only see the onboardings that {me?.email && <span className="font-mono">{me.email}</span>} has been invited to. Other clients&apos; data and the Sentry Cloud IT admin area are not visible to you.
          </div>
        </div>

        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-brand-primary">Your onboarding{items.length > 1 ? "s" : ""}</h1>
          <p className="text-slate-600 mt-2">Click below to continue. Your answers save automatically.</p>
        </div>

        {items.length === 0 ? (
          <Card className="bg-white border-slate-200 p-12 text-center shadow-sm">
            <Building2 className="mx-auto text-slate-400" size={40}/>
            <h3 className="mt-4 text-lg font-medium">No onboardings assigned to your account</h3>
            <p className="text-slate-500 mt-1">If you expected one, please contact your Sentry Cloud IT contact.</p>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="client-onboardings-list">
            {items.map(o => {
              const s = STATUS[o.status] || STATUS.not_started;
              return (
                <button key={o.id} onClick={() => nav(`/onboarding/jwt/${o.id}`)} className="w-full text-left" data-testid={`client-onb-${o.id}`}>
                  <Card className="bg-white border-slate-200 p-5 hover:border-[#00C7FF] hover:shadow transition group shadow-sm">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <div className="font-semibold text-lg text-brand-primary">{o.company_name}</div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>
                        </div>
                        <div className="text-sm text-slate-500 mt-1">Last updated {new Date(o.updated_at).toLocaleDateString()}</div>
                      </div>
                      <ChevronRight size={20} className="text-slate-400 group-hover:text-[#00C7FF] transition shrink-0"/>
                    </div>
                  </Card>
                </button>
              );
            })}
          </div>
        )}
      </main>
      <BrandFooter />
    </div>
  );
}
